import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Description from 'components/Description';
import CenteredContent from 'components/CenteredContent/CenteredContent';
import StickyHeader from 'components/StickyHeader/StickyHeader';
import FormLabel from 'components/FormLabel';
import FormField from 'components/FormField';
import { Info } from 'theme/icons';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import i18n from 'i18n/i18n';
import _ from 'lodash';
import SCHEMA from './SmartFieldsSchema';
import { updateSmartFields } from 'redux/modules/companies';
import { history, setConfirmationFunction } from 'redux/store';
import { showModal } from 'redux/modules/modal';
import { typingDebounceTimeout } from 'theme/uiConfig';

const localize = i18n.loc.localize;

const styles = theme => ({
	stickyHeader: {
		paddingTop: 0,
		marginBottom: 80,
		'&:after': {
			width: 'auto',
			margin: '0 5%',
			[theme.breakpoints.up('md')]: {
				margin: '0 9%'
			}
		}
	},
	header: {
		background: `linear-gradient(to bottom, #eee ${9 * theme.spacing.unit}px, #fff ${9 * theme.spacing.unit}px)`
	},
	centeredContent: {
		marginTop: 0,
		paddingTop: 25,
	},
	descriptionContainer: {
		marginTop: 50
	},
	descriptionInfo: {
		marginLeft: 'auto'
	},
	descriptionInfoIcon: {
		color: theme.palette.colors.white,
		backgroundColor: theme.palette.colors.epsilon,
		borderColor: theme.palette.colors.epsilon,
		padding: 0,
		marginRight: 6,
		'&:hover': {
			backgroundColor: `${theme.palette.colors.epsilonHover} !important`,
			borderColor: `${theme.palette.colors.epsilonHover} !important`
		}
	},
	buttonContainer: {
		paddingBottom: 20
	},
	section: {
		padding: '10px 0 22px'
	},
	sectionLabel: {
		fontSize: 18,
		marginTop: 0,
		color: theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.9)
	},
	fieldDescription: {
		fontSize: 16,
		marginBottom: 12,
		marginLeft: 18,
	},
	field: {
		marginBottom: 5,
		width: 340
	},
	inputWidth: {
		width: 340
	},
	popover: {
		pointerEvents: 'none'
	}
});

const onLeaveMessage = localize('leave-page-confirmation');

@withStyles(styles)
export class SmartFields extends Component {

	constructor(props) {
		super();
		this.validateFields = _.debounce(this.validateFields.bind(this), typingDebounceTimeout);
		this.onBeforeUnload = this.onBeforeUnload.bind(this);
		this.state = {
			...this.getStateFromProps(props)
		};
	}

	getStateFromProps(props) {
		return {
			tab: 0,
			infoAnchor: null,
			button: false,
			values: _.clone(props.smartFields),
			errors: {},
			dirty: {}
		};
	}

	componentWillReceiveProps(props) {
		this.setState(this.getStateFromProps(props));
	}

	componentDidMount() {
		setConfirmationFunction((message, callback) => {
			this.doIfCanLeave(callback);
		});
		this.historyUnblock = history.block('');
		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	componentWillUnmount() {
		this.historyUnblock();
		setConfirmationFunction(null);
		window.removeEventListener('beforeunload', this.onBeforeUnload);
	}

	onBeforeUnload(e) {
		if (this.areUnsavedChanges) {
			let dialogText = onLeaveMessage;
			e.returnValue = dialogText;
			return dialogText;
		}
	}

	render() {
		const { classes } = this.props;
		const { tab } = this.state;
		return (
			<Fragment>
				<StickyHeader className={classes.stickyHeader}>
					<div className={classes.header}>
						<CenteredContent className={classes.centeredContent} padding={false}>
							<Tabs value={tab} onChange={(event, tab) => this.setState({ tab })}>
								<Tab
									component='div'
									label={localize('smartfields-tab')}
									disableRipple
								/>
							</Tabs>
							{tab === 0 && this.renderCompanySmartFieldsHeader()}
						</CenteredContent>
					</div>
				</StickyHeader>
				<CenteredContent padding={false}>
					{tab === 0 && this.renderCompanySmartFieldsContent()}
				</CenteredContent>
			</Fragment>
		);
	}

	renderCompanySmartFieldsHeader() {
		const { classes } = this.props;
		const { infoAnchor } = this.state;
		return (
			<Fragment>
				<Grid container classes={{ container: classes.descriptionContainer }}>
					<Grid item>
						<Description variant='body2'>
							{localize('smartfields-description')}
						</Description>
					</Grid>
					<Grid item className={classes.descriptionInfo}>
						<IconButton
							className={classes.descriptionInfoIcon}
							color='primary'
							component='span'
							disableRipple
							aria-owns={infoAnchor ? 'info-popover' : undefined}
							aria-haspopup='true'
							onMouseEnter={event => this.toggleInfoPopover(event)}
							onMouseLeave={() => this.toggleInfoPopover()}
						>
							<Info />
						</IconButton>
						<Popover
							id='info-popover'
							className={classes.popover}
							open={infoAnchor !== null}
							anchorEl={infoAnchor}
							anchorOrigin={{
								vertical: 'center',
								horizontal: 'left'
							}}
							transformOrigin={{
								vertical: 'center',
								horizontal: 'right'
							}}
							disableRestoreFocus
							onClose={() => this.toggleInfoPopover()}
						>
							<Typography variant='body2' dangerouslySetInnerHTML={{ __html: localize('smartfields-info') }} />
						</Popover>
					</Grid>
				</Grid>
				{this.button()}
			</Fragment>
		);
	}

	renderCompanySmartFieldsContent() {
		return (
			<Fragment>
				{this.renderSection(localize('smartfields-team-name'), SCHEMA.COMPANY_NAME)}
				{this.renderSection(localize('smartfields-email'), SCHEMA.EMAIL)}
				{this.renderSection(localize('smartfields-address'), SCHEMA.ADDRESS)}
				{this.renderSection(localize('smartfields-phone'), SCHEMA.PHONE_NUMBER)}
				{this.renderSection(localize('smartfields-website'), SCHEMA.WEBSITE)}
				{this.renderSection(localize('smartfields-instagram'), SCHEMA.INSTAGRAM)}
				{this.renderSection(localize('smartfields-facebook'), SCHEMA.FACEBOOK)}
				{this.renderSection(localize('smartfields-twitter'), SCHEMA.TWITTER)}
				{this.renderSection(localize('smartfields-linkedin'), SCHEMA.LINKEDIN)}
			</Fragment>
		);
	}

	button() {
		const { classes } = this.props;
		const errors = this.areErrors;
		const disabled = !this.state.button || errors || !this.areUnsavedChanges;

		return (
			<Grid container className={classes.buttonContainer}>
				<Grid item xs={12}>
					<Button
						variant='contained'
						color='primary'
						size='large'
						data-tid="button-smartfields-save"
						disabled={disabled}
						onClick={() => this.handleSave()}
					>
						{localize('save')}
					</Button>
				</Grid>
				{errors && <Grid item xs={12}><FormHelperText error>{localize('smartfields-error')}</FormHelperText></Grid>}
			</Grid>
		);
	}

	renderSection(title, fields = []) {
		const { classes } = this.props;
		return (

			<Grid container classes={{ container: classes.section }}>
				<FormLabel title={title} className={classes.sectionLabel}>{title}</FormLabel>
				{fields.map(field => (
					<Grid key={field.key} item xs={12}>
						{field.description && <FormHelperText className={classes.fieldDescription}>
							{field.description}
						</FormHelperText>}
						{this.renderField(field)}
					</Grid>
				), this)}
			</Grid>
		);
	}

	renderField(field) {
		const { classes } = this.props;
		const { key, placeholder = '', prefix = '' } = field;
		let value = this.state.values[key];

		return (
			<FormField
				name={key}
				data-tid={`form-smartfields-${key}`}
				value={value}
				placeholder={placeholder}
				onChange={event => this.onChange(event, key, prefix)}
				helperText={this.state.errors[key]}
				error={!!this.state.errors[key]}
				className={classes.field}
			/>
		);
	}

	onChange(event, key, prefix) {
		const { values, dirty, errors } = this.state;
		const { smartFields } = this.props;
		let { value } = event.target;

		// Add prefix if it's not provided
		if (value && value.charAt(0) !== prefix) {
			value = prefix + value;
		}

		// Delete error when typing; value will be validated in validateFields
		if (errors[key]) {
			delete errors[key];
		}

		this.setState({
			button: false,
			values: {
				...values,
				[key]: value
			},
			dirty: {
				...dirty,
				[key]: value !== smartFields[key] // means changed value
			},
			errors
		}, this.validateFields);
	}

	validateFields() {
		const errors = {};

		for (const id of Object.keys(SCHEMA)) {
			for (const field of SCHEMA[id]) {
				const { key, validator } = field;
				if (validator) {
					const error = validator(this.state.values[key], this.state.dirty[key]);
					if (error) {
						errors[key] = error;
					}
				}
			}
		}

		this.setState({ button: true, errors });
	}

	toggleInfoPopover(event = { target: null }) {
		this.setState({ infoAnchor: event.target });
	}

	handleSave() {
		const { values, dirty } = this.state;
		const smartFields = {};

		for (const key of Object.keys(values)) {
			const value = values[key];

			// Transform only those smart fields that are touched (value changed)
			if (dirty[key]) {
				smartFields[key] = value;
			}
		}

		!_.isEmpty(smartFields) && this.props.updateSmartFields(smartFields);
	}

	doIfCanLeave(callback) {
		const { dirty } = this.state;

		// No changes, leave the page
		if (_.isEmpty(dirty)) {
			callback(true);
			return;
		}

		this.props.showModal('CONFIRMATION_MODAL', {
			message: onLeaveMessage,
			buttons: [
				<Button
					key='button-cancel'
					variant='outlined'
					color='primary'
					data-tid="button-smartfields-company-stay"
					onClick={() => {callback(false);}}
				>
					{localize('stay-on-page')}
				</Button>,
				<Button
					key='button-confirm'
					variant='contained'
					color='primary'
					data-tid="button-smartfields-company-leave"
					onClick={() => {callback(true);}}
				>
					{localize('leave-page')}
				</Button>
			]
		});
	}

	get areUnsavedChanges() {
		const { dirty } = this.state;
		return Object.keys(_.pickBy(dirty)).length > 0;
	}

	get areErrors() {
		const { errors } = this.state;
		return Object.keys(errors).length > 0;
	}

}

SmartFields.propTypes = {
	classes: PropTypes.object,
	smartFields: PropTypes.object,
	updateSmartFields: PropTypes.func,
	showModal: PropTypes.func
};

const mapStateToProps = state => ({
	smartFields: _.get(state.companies.rawDataById, `${state.companies.selectedId}.smartFields`, {})
});

const mapDispatchToProps = dispatch => bindActionCreators({
	updateSmartFields,
	showModal
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SmartFields);



// WEBPACK FOOTER //
// ./src/containers/SmartFields/SmartFields.js